import React, { useState, useEffect } from "react";
import { X } from "lucide-react";

export default function CookieBanner() {
  const [visible, setVisible] = useState(false);
  const [showPreferences, setShowPreferences] = useState(false);

  useEffect(() => {
    const consent = localStorage.getItem("cookie_consent");
    if (!consent) {
      const timer = setTimeout(() => setVisible(true), 800);
      return () => clearTimeout(timer);
    }
  }, []);

  const handleAction = (type) => {
    localStorage.setItem("cookie_consent", type);
    setVisible(false);
    setShowPreferences(false);
  };

  if (!visible) return null;

  return (
    <>
      {/* 1. MAIN BOTTOM BAR */}
      <div className="fixed bottom-0 left-0 right-0 z-[9998] bg-white border-t border-gray-200 shadow-[0_-2px_10px_rgba(0,0,0,0.05)] animate-in fade-in slide-in-from-bottom-2 duration-1000 ease-out">
        <div className="max-w-[1400px] mx-auto px-6 py-4 md:py-5 flex flex-col md:flex-row items-start md:items-center justify-between gap-6 relative">
          <div className="flex-1 pr-10">
            <h2 className="text-lg md:text-xl font-bold text-[#333] mb-1">
              This Website Uses Cookies
            </h2>
            <p className="text-[12px] md:text-[13px] text-[#666] leading-relaxed max-w-5xl">
              This site uses cookies to store information on your computer. You
              may enable or disable certain cookies by clicking "Show Details"
              or as further described in our Cookie Policy. Please read our{" "}
              <span className="inline-block px-2 py-0.5 bg-gray-100 text-[#0056b3] text-[11px] font-medium rounded border border-gray-200 cursor-default select-none">
                Cookie Policy
              </span>{" "}
              to learn more.
            </p>
          </div>

          <div className="flex items-center gap-3 w-full md:w-auto">
            <button
              onClick={() => setShowPreferences(true)}
              className="flex-1 md:flex-none px-6 py-2 border border-gray-800 text-[#0056b3] text-sm font-semibold rounded hover:bg-gray-50 transition-colors whitespace-nowrap"
            >
              Show Details
            </button>
            <button
              onClick={() => handleAction("accepted")}
              className="flex-1 md:flex-none px-10 py-2 bg-[#0056b3] hover:bg-[#004494] text-white text-sm font-bold rounded transition-colors whitespace-nowrap shadow-sm"
            >
              Accept
            </button>
          </div>

          <button
            onClick={() => setVisible(false)}
            className="absolute top-4 right-4 text-gray-400 hover:text-gray-900"
          >
            <X className="w-5 h-5 stroke-[2.5px]" />
          </button>
        </div>
      </div>

      {/* 2. SIDE PREFERENCE PANEL */}
      <div
        className={`fixed inset-0 z-[9999] overflow-hidden transition-opacity duration-300 ${
          showPreferences
            ? "opacity-100 pointer-events-auto"
            : "opacity-0 pointer-events-none"
        }`}
      >
        {/* Backdrop */}
        <div
          className="absolute inset-0 bg-black/40 transition-opacity duration-300"
          onClick={() => setShowPreferences(false)}
        />

        {/* LEFT SLIDE PANEL */}
        <div
          className={`fixed top-0 left-0 z-[10000] h-screen w-[380px] max-w-full bg-white shadow-2xl transition-transform duration-500 ease-in-out flex flex-col ${
            showPreferences ? "translate-x-0" : "-translate-x-full"
          }`}
        >
          {/* Scrollable Content Area */}
          <div className="flex-1 overflow-y-auto">
            {/* Header */}
            <div className="p-6 pb-4">
              <div className="flex items-start justify-between mb-4">
                <div className="flex items-center gap-2">
                  {/* Synthera Logo */}
                  <div className="flex items-center">
                    <span className="text-xl font-bold text-[#003B5C] tracking-tight">
                      Synthera Security
                    </span>
                  </div>
                </div>
                <button
                  onClick={() => setShowPreferences(false)}
                  className="p-1 hover:bg-gray-100 rounded border border-gray-300"
                >
                  <X className="w-4 h-4 text-gray-500" />
                </button>
              </div>

              <h2 className="text-xl font-bold text-gray-800 mb-2">
                Privacy Preference Center
              </h2>

              <p className="text-[11px] text-gray-500 leading-relaxed mb-2">
                Cookies are small text files that can be used by websites to
                make a user's experience more efficient.
              </p>
              <p className="text-[11px] text-gray-500 leading-relaxed mb-2">
                The law states that we can store cookies on your device if they
                are strictly necessary for the operation of this site. For all
                other types of cookies we need your permission.
              </p>
              <p className="text-[11px] text-gray-500 leading-relaxed mb-2">
                This site uses different types of cookies. Some cookies are
                placed by third party services that appear on our pages.
              </p>
              <p className="text-[11px] text-gray-500 leading-relaxed mb-2">
                You can at any time change or withdraw your consent from the
                Cookie Declaration on our website.
              </p>
              <p className="text-[11px] text-gray-500 leading-relaxed mb-3">
                Learn more about who we are, how you can contact us and how we
                process personal data in our{" "}
                <span className="inline-block px-2 py-0.5 bg-gray-100 text-[#0056b3] text-[11px] font-medium rounded border border-gray-200 cursor-default select-none">
                  Privacy Policy
                </span>
                .
              </p>

              <span className="inline-block px-3 py-1.5 bg-gray-100 text-[#0056b3] text-[11px] font-medium rounded border border-gray-200 cursor-default select-none mb-4">
                Cookie Policy
              </span>

              <button
                onClick={() => handleAction("accepted")}
                className="block bg-[#0056b3] text-white px-6 py-2.5 rounded font-semibold text-sm hover:bg-[#004494] transition-colors"
              >
                Allow All
              </button>
            </div>

            {/* Divider */}
            <div className="border-t border-gray-200 mx-6" />

            {/* Cookie Categories */}
            <div className="px-6 py-5">
              <h3 className="text-sm font-bold text-gray-800 mb-6">
                Manage Consent Preferences
              </h3>

              <div className="space-y-6">
                {/* Necessary */}
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <h4 className="font-bold text-[13px] text-gray-800">
                      Necessary Cookies
                    </h4>
                    <span className="text-[#0056b3] text-[11px] font-semibold">
                      Always Active
                    </span>
                  </div>
                  <p className="text-[11px] text-gray-500 leading-relaxed">
                    Necessary cookies help make a website usable by enabling
                    basic functions like page navigation and access to secure
                    areas of the website. The website cannot function properly
                    without these cookies.
                  </p>
                </div>

                {/* Analytics */}
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <h4 className="font-bold text-[13px] text-gray-800">
                      Analytics Cookies
                    </h4>
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input type="checkbox" className="sr-only peer" />
                      <div className="w-9 h-5 bg-gray-300 rounded-full peer peer-checked:bg-gray-500 after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:after:translate-x-4"></div>
                    </label>
                  </div>
                  <p className="text-[11px] text-gray-500 leading-relaxed">
                    Analytics cookies (often with the help of a third-party
                    service provider) help us collect information on how users
                    interact with our websites, including, for example, pages
                    visited, sites visited from, and browser, operating system,
                    and device information. We use information collected via
                    analytics to improve how our websites function and to
                    understand how users interact with them. Some of our
                    analytics providers offer opt-out mechanisms for certain
                    cookies. For more information, please see our Cookies
                    Policy.
                  </p>
                </div>

                {/* Preferences */}
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <h4 className="font-bold text-[13px] text-gray-800">
                      Preferences Cookies
                    </h4>
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input type="checkbox" className="sr-only peer" />
                      <div className="w-9 h-5 bg-gray-300 rounded-full peer peer-checked:bg-gray-500 after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:after:translate-x-4"></div>
                    </label>
                  </div>
                  <p className="text-[11px] text-gray-500 leading-relaxed">
                    Preference cookies enable a website to remember information
                    that changes the way the website behaves or looks, like your
                    preferred language or the region that you are in.
                  </p>
                </div>

                {/* Marketing */}
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <h4 className="font-bold text-[13px] text-gray-800">
                      Marketing Cookies
                    </h4>
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input type="checkbox" className="sr-only peer" />
                      <div className="w-9 h-5 bg-gray-300 rounded-full peer peer-checked:bg-gray-500 after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:after:translate-x-4"></div>
                    </label>
                  </div>
                  <p className="text-[11px] text-gray-500 leading-relaxed">
                    Marketing cookies are used to track visitors across
                    websites. The intention is to display ads that are relevant
                    and engaging for the individual user and thereby more
                    valuable for publishers and third party advertisers.
                  </p>
                </div>

                {/* Third Party / Embedded Content */}
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <h4 className="font-bold text-[13px] text-gray-800">
                      Third Party / Embedded Content Cookies
                    </h4>
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input type="checkbox" className="sr-only peer" />
                      <div className="w-9 h-5 bg-gray-300 rounded-full peer peer-checked:bg-gray-500 after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:after:translate-x-4"></div>
                    </label>
                  </div>
                  <p className="text-[11px] text-gray-500 leading-relaxed">
                    Our websites use third-party widgets and apps to enhance the
                    user experience, including integrations with social media
                    platforms (e.g., through the use of sharing buttons). As a
                    result, cookies may be set by these third parties, and used
                    by them to track your online activity for their own purposes
                    (which may include advertising). Please refer to the privacy
                    policies or cookie policies accessible on those services for
                    more information.
                  </p>
                </div>
              </div>
            </div>

            {/* Spacer for footer */}
            <div className="h-24" />
          </div>

          {/* Footer - Fixed at bottom */}
          <div className="absolute bottom-0 left-0 right-0 border-t border-gray-200 bg-white p-6">
            <button
              onClick={() => handleAction("custom")}
              className="w-full bg-[#0056b3] hover:bg-[#004494] text-white py-3 rounded font-bold text-sm transition-colors"
            >
              Confirm My Choices
            </button>

            <p className="text-[10px] text-gray-400 text-center mt-4 flex items-center justify-center gap-1">
              Powered by <span className="font-bold text-gray-500">(f)</span>{" "}
              OneTrust
            </p>
          </div>
        </div>
      </div>
    </>
  );
}
